/**
 * VerifyBanner.tsx
 * Shows a gentle yellow banner when email is not verified.
 * Non-blocking — user can still use the app.
 */
import React, { useState } from 'react';
import { Mail, X, Check, Loader } from 'lucide-react';
import { useStore } from '../../store';
import { authApi } from '../../api/client';

const C = { amber100:'#FFF4E0', amber200:'#FFD88A', amber300:'#F5A623', amber400:'#C4811A', amber500:'#8B5A0A', ink:'#1A1814', muted:'#7A7570', white:'#FFFFFF' };

const VerifyBanner: React.FC = () => {
  const user = useStore(s => s.user);
  const [dismissed, setDismissed] = useState(false);
  const [sending,   setSending]   = useState(false);
  const [sent,      setSent]      = useState(false);

  // Don't show if: no user, already verified, or dismissed
  if (!user || user.emailVerified || dismissed) return null;

  const resend = async () => {
    setSending(true);
    try {
      await authApi.resendVerification();
      setSent(true);
      setTimeout(() => setSent(false), 4000);
    } catch { setSent(true); setTimeout(() => setSent(false), 4000); }
    finally { setSending(false); }
  };

  return (
    <div style={{ background:C.amber100, borderBottom:`1px solid ${C.amber200}`, padding:'10px clamp(16px,3vw,24px)', display:'flex', alignItems:'center', gap:12, flexWrap:'wrap', zIndex:50 }}>
      <Mail size={15} color={C.amber500} style={{ flexShrink:0 }}/>
      <p style={{ fontSize:13, color:C.amber500, fontFamily:"'DM Sans',sans-serif", fontWeight:600, flex:1, minWidth:200 }}>
        Please verify your email address to secure your account.
      </p>
      <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
        {sent ? (
          <span style={{ display:'flex', alignItems:'center', gap:5, fontSize:12, color:'#16A34A', fontFamily:"'DM Sans',sans-serif", fontWeight:600 }}>
            <Check size={13}/> Email sent! Check your inbox.
          </span>
        ) : (
          <button
            onClick={resend} disabled={sending}
            style={{ display:'flex', alignItems:'center', gap:5, padding:'5px 12px', borderRadius:7, border:`1.5px solid ${C.amber300}`, background:C.white, cursor:'pointer', fontSize:12, fontFamily:"'DM Sans',sans-serif", fontWeight:700, color:C.amber500 }}
          >
            {sending ? <><Loader size={11} style={{ animation:'spin .7s linear infinite' }}/> Sending…</> : 'Resend email'}
          </button>
        )}
        <button onClick={() => setDismissed(true)} style={{ background:'none', border:'none', cursor:'pointer', color:C.amber500, padding:3, display:'flex' }}>
          <X size={14}/>
        </button>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

export default VerifyBanner;